/**
 * EmptyState Component
 *
 * Displays a message when no data is available
 */

import '@tylertech/forge/icon';
import '@tylertech/forge/button';
import './EmptyState.css';

export interface EmptyStateProps {
  /** Icon name to display (e.g., 'inbox', 'search', 'filter_list') */
  icon?: string;
  /** Primary message */
  title: string;
  /** Secondary description text */
  description?: string;
  /** Action button label */
  actionLabel?: string;
  /** Action button click handler */
  onAction?: () => void;
}

/**
 * Empty state placeholder with optional icon and action button
 *
 * @example
 * ```tsx
 * <EmptyState
 *   icon="inbox"
 *   title="No services found"
 *   description="Create your first service to get started"
 *   actionLabel="Create Service"
 *   onAction={() => navigate('/services/new')}
 * />
 * ```
 */
export function EmptyState({
  icon = 'inbox',
  title,
  description,
  actionLabel,
  onAction,
}: EmptyStateProps): JSX.Element {
  const IconElement = 'forge-icon' as React.ElementType;
  const ButtonElement = 'forge-button' as React.ElementType;

  return (
    <div className="empty-state">
      <IconElement name={icon} className="empty-state-icon" />
      <h3 className="empty-state-title">{title}</h3>
      {description && <p className="empty-state-description">{description}</p>}
      {actionLabel && onAction && (
        <ButtonElement type="button" variant="raised" onClick={onAction}>
          {actionLabel}
        </ButtonElement>
      )}
    </div>
  );
}
